<template>
  <div class="public-tracking-page">
    <div class="container-fluid">
      <b-row>
        <b-col cols="12" md="6" lg="4" xl="3">
          <FormulateInput
            v-model="form.route"
            type="select-resourceful"
            name="nameOfRoute"
            :label="$t('global.object.nameOfRoute')"
            :placeholder="`${$t('global.form.select')} ${$t('global.object.nameOfRoute')}`"
            :optionsGetter="
              (queryParam) => routeApi.getSearchByName({ ...queryParam, filters: routeFilters })
            "
            :optionLabelFn="
              ({ item }) => {
                return item.name
              }
            "
          />
        </b-col>
      </b-row>
    </div>

    <MapLibre
      :geofences="geofences"
      :vehicle-statuses="vehicleStatuses"
      :show-only-last="showOnlyLastGeofences"
      :loading="loadingRoute"
      :saved-zoom="savedMapZoom"
      :saved-center="savedMapCenter"
      :polyline="polyline"
      @zoom-changed="onMapZoomChanged"
      @center-changed="onMapCenterChanged"
    />

    <div class="public-tracking-footer">
      <figure class="d-inline-block mb-0">
        <img ref="imgCopyrightLogo" :src="getAsset('primaryLogo', 'login')" alt />
      </figure>
    </div>
  </div>
</template>

<script>
import appConfig from '@/app.config'
import MapLibre from './components/MapLibre'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'
import * as routeApi from '@/services/rest-api-vsms/lib/route/route'
import { GET_ROUTE_RECORDS } from '@/services/api/route'
import { GET_VEHICLE_STATUSES_LIVEVIEW } from '@/services/api/fleet'
import { GET_PAYLOAD } from '@/services/api/auth'
import { GET_ROUTE_SETTING } from '@/services/api/route'

export default {
  page: {
    title: 'Route Tracking',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    MapLibre,
  },
  mixins: [Helper],
  data() {
    return {
      form: {
        route: null,
      },
      routeApi: routeApi,
      routeFilters: "status eq 'I'",
      geofences: [],
      showOnlyLastGeofences: true,
      loadingRoute: false,
      routeRecords: {},
      vehicleIds: [],
      vehicleStatuses: [],
      savedMapZoom: null,
      savedMapCenter: null,
      polyline: null,
      // WebSocket GPS related
      wsPayload: null,
      forceDisconnect: false,
    }
  },
  computed: {
    ...mapGetters({
      getAsset: 'asset/getAsset',
    }),
    resourcefulQuery() {
      return {
        vehicles: this.getVehicles,
      }
    },
  },
  watch: {
    'form.route': {
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.savedMapZoom = null
          this.savedMapCenter = null
          this.getRoute(newVal)
        } else if (!newVal) {
          this.geofences = []
          this.vehicleStatuses = []
          this.polyline = null
          this.savedMapZoom = null
          this.savedMapCenter = null
          this.disconnectWebSocket()
        }
      },
      immediate: false,
    },
  },
  mounted() {
    this.adjustImageSize()
    this.fetchRouteSetting()
  },
  beforeDestroy() {
    this.disconnectWebSocket()
  },
  methods: {
    adjustImageSize() {
      if (this.$refs.imgCopyrightLogo) {
        this.resizeImage(this.$refs.imgCopyrightLogo, 21, 125)
      }
    },

    async fetchRouteSetting() {
      try {
        this.$store.dispatch('route/setLoadingSetting', true)

        const res = await this.$apollo.query({
          fetchPolicy: 'no-cache',
          query: GET_ROUTE_SETTING,
        })

        if (res.data?.routeSetting?.isSuccessful && res.data?.routeSetting?.routeSetting) {
          const settings = res.data.routeSetting.routeSetting

          this.$store.dispatch('route/setRouteSetting', {
            isLicensePlateVisible: settings.licensePlateVisibility === 'shown',
            usingArrow: settings.markerType === 'arrow',
          })
        }
      } catch (error) {
        // Fallback to default values if API fails
        this.$store.dispatch('route/setRouteSetting', {
          usingArrow: false,
          isLicensePlateVisible: true,
        })
      } finally {
        this.$store.dispatch('route/setLoadingSetting', false)
      }
    },

    onMapZoomChanged(zoom) {
      this.savedMapZoom = zoom
    },

    onMapCenterChanged(center) {
      this.savedMapCenter = center
    },

    disconnectWebSocket() {
      this.forceDisconnect = true
      if (this.$socket) {
        this.$socket.close()
      }
    },

    async getEncrypDataWsGps() {
      if (!this.vehicleIds.length) return

      try {
        const res = await this.$apollo.query({
          query: GET_PAYLOAD,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              gpsVehicleIds: this.vehicleIds,
              filterByType: ['GPS'],
            },
          },
        })
        this.wsPayload = res.data.getPayload.payload.payload
      } catch (error) {
        this.wsPayload = null
        // Silent fail for GPS payload
      }
    },

    async startWebSocket() {
      if (!this.wsPayload) return

      try {
        // Reset force disconnect flag
        this.forceDisconnect = false

        const obj = {
          type: 'update_tags',
          data: this.wsPayload,
        }

        await this.$store.dispatch(
          'ws/WSConnect',
          `${process.env.VUE_APP_WS_URL}/ws?token=${localStorage.getItem('wsToken')}`
        )

        this.$socket.onopen = () => {
          this.$socket.sendObj(obj)
        }

        this.$socket.onmessage = (dataWs) => {
          const data = JSON.parse(dataWs.data)
          if (data?.type?.startsWith('gps-')) {
            const vehicleId = parseInt(data.type.slice(4))
            // Only update if this vehicle is in our current route
            if (this.vehicleIds.includes(vehicleId) && data.payload) {
              this.updateVehicleFromWebSocket(vehicleId, data.payload)
            }
          }
        }

        this.$socket.onclose = async () => {
          if (this.forceDisconnect) return
          // Reconnect after a delay
          setTimeout(() => {
            if (this.wsPayload) {
              this.startWebSocket()
            }
          }, 5000)
        }
      } catch (error) {
        // Silent fail for WebSocket connection
      }
    },

    updateVehicleFromWebSocket(vehicleId, payload) {
      const vehicleIndex = this.vehicleStatuses.findIndex(
        (v) => parseInt(v.vehicleId) === parseInt(vehicleId)
      )

      if (vehicleIndex !== -1) {
        const updatedVehicle = {
          ...this.vehicleStatuses[vehicleIndex],
          latitude: parseFloat(payload.latitude),
          longitude: parseFloat(payload.longitude),
          direction:
            payload.direction !== undefined
              ? parseFloat(payload.direction)
              : this.vehicleStatuses[vehicleIndex].direction,
          lastUpdateTime: new Date().toISOString(),
        }

        this.$set(this.vehicleStatuses, vehicleIndex, updatedVehicle)
      }
    },

    async getRoute(routeId) {
      if (!routeId) {
        return
      }

      try {
        this.loadingRoute = true
        this.geofences = []
        this.vehicleStatuses = []
        this.polyline = null
        this.disconnectWebSocket()

        const res = await routeApi.show(routeId)
        if (
          res &&
          res.data &&
          res.data.route_plan &&
          res.data.route_plan[0] &&
          res.data.route_plan[0].geofences
        ) {
          this.geofences = res.data.route_plan[0].geofences

          await Promise.all([this.getRouteRecords(), this.getRoutePolylines(routeId)])
        } else {
          this.geofences = []
        }
      } catch (error) {
        this.geofences = []
        this.vehicleStatuses = []
        this.polyline = null
        this.disconnectWebSocket()
      } finally {
        this.loadingRoute = false
      }
    },

    async getRoutePolylines(routeId) {
      try {
        const res = await routeApi.showDirection(routeId)
        if (!res?.data) return

        // Parse primary route only
        if (res.data.route_plan?.[0]?.geometry) {
          const primaryDirection = JSON.parse(res.data.route_plan[0].geometry)
          const primaryLine = primaryDirection.map(([long, lat]) => ({
            lat: lat,
            lng: long,
          }))

          this.polyline = {
            path: primaryLine,
            options: {
              strokeColor: '#0D4491',
              strokeOpacity: 1.0,
              strokeWeight: 6,
              zIndex: 99999,
            },
          }
        }
      } catch (error) {
        this.polyline = null
      }
    },

    async getRouteRecords() {
      try {
        const filters = {
          status: 'progress',
          routeId: this.form.route,
          pagination: {
            page: 1,
            show: 9999,
          },
        }

        const res = await this.$apollo.query({
          fetchPolicy: 'no-cache',
          query: GET_ROUTE_RECORDS,
          variables: {
            filter: filters,
          },
        })

        this.routeRecords = res.data.routeRecords.routeRecords.data
        this.vehicleIds = this.routeRecords.map((record) => record.vehicle.id)

        if (this.vehicleIds.length > 0) {
          await Promise.all([this.getVehicles(), this.getEncrypDataWsGps()])

          this.startWebSocket()
        }
      } catch (error) {
        this.vehicleStatuses = []
        this.disconnectWebSocket()
      }
    },

    async getVehicles() {
      try {
        const res = await this.$apollo.query({
          fetchPolicy: 'no-cache',
          query: GET_VEHICLE_STATUSES_LIVEVIEW,
          variables: {
            filter: {
              vehicleId: this.vehicleIds,
            },
          },
        })

        if (!res.data.vehicleStatusesLiveview.isSuccessful) {
          this.vehicleStatuses = []
          return
        }

        this.vehicleStatuses = res.data.vehicleStatusesLiveview.vehicleStatusesLiveview
      } catch (error) {
        this.vehicleStatuses = []
        // eslint-disable-next-line no-console
        console.error('Error fetching vehicle statuses:', error)
      }
    },
  },
}
</script>
